import { observable, runInAction, action, makeObservable } from 'mobx'
import SilentListStore from 'stores/SilentListStore'
import Sorter from 'models/Sorter'
import ComunicationsService from 'services/ComunicationsService'
import Paginator from 'models/Paginator'

class ComunicationsStore extends SilentListStore {
    DEFAULT_SORT_FIELD = 'createdAt'
    DEFAULT_SORT_SENSE = 'DESC'
    currentPage = 1
    comunications = []
    comunication = null
    error = null
    paginator = Paginator.empty()
    filterSelectedReaded = 'all'
    textSearch = ''
    sorter = Sorter.fromJson({
        sortField: this.DEFAULT_SORT_FIELD,
        sortSense: this.DEFAULT_SORT_SENSE,
    })

    carouselImages = []

    constructor(loadListOnInit = true) {
        super({ itemsKey: 'comunications' })

        makeObservable(this, {
            // observable
            currentPage: observable,
            comunications: observable,
            comunication: observable,
            paginator: observable,
            filterSelectedReaded: observable,
            textSearch: observable,
            sorter: observable,
            error: observable,
            carouselImages: observable,
            // action
            changePage: action,
            getComunicationsList: action,
            getComunication: action,
            setFilterSelectedReaded: action,
            setTextSearch: action,
            changeSort: action,
            setCarouselImages: action,
            getCarouselImages: action,
            ...this.getSilentListAnnotations(),
        })

        this.comunicationsService = new ComunicationsService()

        if (loadListOnInit) {
            this.init()
        }
    }

    getListCacheKey() {
        return [
            this.filterSelectedReaded || '',
            this.textSearch || '',
            this.currentPage,
            this.sorter.sortField,
            this.sorter.sortSense,
        ].join('|')
    }

    setTextSearch(text) {
        this.textSearch = text
        this.currentPage = 1
    }

    setFilterSelectedReaded(filter) {
        if (this.filterSelectedReaded === filter) {
            return false
        }

        this.filterSelectedReaded = filter
        this.currentPage = 1

        return true
    }

    changeSort(sortField) {
        this.sorter.changeSort(sortField)
        this.currentPage = 1
    }

    init() {
        this.getComunicationsList()
    }

    changePage(page) {
        this.currentPage = page
    }

    async getComunicationsList({ silent = false } = {}) {
        await this.fetchList({
            silent,
            onRetry: () => this.getComunicationsList(),
            request: async () => {
                const read =
                    this.filterSelectedReaded === 'all'
                        ? undefined
                        : this.filterSelectedReaded === 'read'

                const params = {
                    page: this.currentPage,
                    sort: { [this.sorter?.sortField]: this.sorter?.sortSense?.toLowerCase() },
                    search: this.textSearch,
                    read,
                }
                const { comunications, paginator } = await this.comunicationsService.listComunications(
                    params
                )

                return {
                    items: comunications,
                    paginator,
                }
            },
        })
    }

    async getComunication(id) {
        this.preRequest(this.getComunication)

        try {
            const comunication = await this.comunicationsService.getComunication(id)

            runInAction(() => {
                this.comunication = comunication

                this.onSuccessRequest()
            })
        } catch (e) {
            runInAction(() => {
                this.error = e
                this.onErrorRequest(e)
            })
        }
    }

    async getDownloadLink(comunicationId, fileId) {
        try {
            return await this.comunicationsService.getComunicationDocumentLink(comunicationId, fileId)
        } catch (e) {
            runInAction(() => {
                this.error = e
                this.onErrorRequest(e)
            })
            return null
        }
    }

    setCarouselImages(value) {
        this.carouselImages = value
    }

    get carouselHasImages() {
        return (
            this.carouselImages.length > 0
        )
    }

    async getCarouselImages() {
        this.preRequest(this.getCarouselImages)

        try {
            const images = await this.comunicationsService.getCarouselImages()
            this.setCarouselImages(images)

            runInAction(() => {
                this.onSuccessRequest()
            })
        } catch (e) {
            runInAction(() => {
                this.error = e
                this.onErrorRequest(e)
            })
        }
    }
}

export default ComunicationsStore
